import React, { useState, useEffect } from 'react';
import {
  getSubmissionListingMode,
  getApprovedRaising,
  normalizeRaisingMode,
  resolveShareStructureClient,
  validateRaisingStructureClient,
  formatListerProposedShareSummary,
  filterNumericInput,
  saleListingNeedsRaisingApproval,
  formatAdminReviewTimestamp,
} from '../../utils/submissions';

const AdminRaiseStructureEditor = ({ sub, onSave, saving, addToast }) => {
  const approved = getApprovedRaising(sub);
  const [expanded, setExpanded] = useState(false);
  const [raisingMode, setRaisingMode] = useState(normalizeRaisingMode(approved.raisingMode));
  const [shareCount, setShareCount] = useState(String(approved.shareCount ?? approved.totalShares ?? "10"));
  const [minInvestment, setMinInvestment] = useState(String(approved.minInvestment ?? "25000"));

  useEffect(() => {
    const a = getApprovedRaising(sub);
    setRaisingMode(normalizeRaisingMode(a.raisingMode));
    setShareCount(String(a.shareCount ?? a.totalShares ?? "10"));
    setMinInvestment(String(a.minInvestment ?? "25000"));
  }, [sub?.id, sub?.raisingMode, sub?.shareCount, sub?.totalShares, sub?.minInvestment]);

  if (getSubmissionListingMode(sub) === "rent") return null;

  const price = Number(sub?.price) || 0;
  const proposedLine = formatListerProposedShareSummary(sub);
  const preview = resolveShareStructureClient({
    price,
    raisingMode,
    shareCount,
    minInvestment
  });
  const isApproved = sub?.raisingStructureApproved === true;
  const needsApproval = saleListingNeedsRaisingApproval(sub);
  const perShare = preview.shares > 0 ? price / preview.shares : 0;

  const handleSave = async () => {
    const check = validateRaisingStructureClient({
      price,
      raisingMode,
      shareCount,
      minInvestment
    });
    if (!check.ok) {
      addToast(check.message, "error");
      return;
    }
    const body =
      raisingMode === "min_investment"
        ? { raisingMode, minInvestment: parseFloat(minInvestment) || 0 }
        : { raisingMode: "co_owners", shareCount: parseInt(shareCount, 10) };
    await onSave(sub.id, body);
  };

  return (
    <div className={`admin-share-structure admin-luxury-collapsible${needsApproval ? " admin-share-structure--needs-approval" : ""}`}>
      <header className="admin-luxury-collapsible__head">
        <button
          type="button"
          className="admin-luxury-collapsible__toggle"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
        >
          <div className="admin-luxury-collapsible__head-main">
            <div className="admin-luxury-collapsible__head-icon admin-luxury-collapsible__head-icon--shares" aria-hidden="true">
              <i className="fas fa-users"></i>
            </div>
            <div>
              <h3>Final share structure</h3>
              <p>Admin only — what investors see when the property goes Live</p>
            </div>
          </div>
          <div className="admin-luxury-collapsible__head-meta">
            {isApproved ? (
              <span className="admin-luxury-collapsible__badge admin-luxury-collapsible__badge--success">Approved for investors</span>
            ) : (
              <span className="admin-luxury-collapsible__badge admin-luxury-collapsible__badge--warning">Approval required</span>
            )}
            <i className={`fas fa-chevron-${expanded ? "up" : "down"} admin-luxury-collapsible__chevron`} aria-hidden="true"></i>
          </div>
        </button>
      </header>

      {expanded ? (
        <div className="admin-luxury-collapsible__body">
          <div className="admin-share-structure__card">
            <div className="admin-share-structure__card-body">
              <div className="admin-share-structure__info-box">
                <span className="admin-share-structure__info-icon" aria-hidden="true">💡</span>
                <p className="admin-share-structure__info-text">
                  You have final say. What you save here is what investors see when the property goes Live on Site
                  {sub.publishedPropertyId ? " (and updates the live listing immediately)" : ""}.
                </p>
              </div>

              {proposedLine ? (
                <div className="admin-share-structure__suggested">
                  <div className="admin-share-structure__suggested-icon" aria-hidden="true">
                    <i className="fas fa-file-alt"></i>
                  </div>
                  <p className="admin-share-structure__suggested-text">
                    <strong>Lister suggested:</strong> {proposedLine}
                  </p>
                </div>
              ) : null}

              <div className="admin-share-structure__radio-group" role="radiogroup" aria-label="Share structure mode">
                <label
                  className={`admin-share-structure__radio-card${raisingMode !== "min_investment" ? " admin-share-structure__radio-card--selected" : ""}`}
                >
                  <input
                    type="radio"
                    name={`admin-raise-${sub.id}`}
                    checked={raisingMode !== "min_investment"}
                    onChange={() => setRaisingMode("co_owners")}
                    className="admin-share-structure__radio-input"
                  />
                  <span className="admin-share-structure__radio-circle" aria-hidden="true">
                    <span className="admin-share-structure__radio-circle-inner"></span>
                  </span>
                  <span className="admin-share-structure__radio-content">
                    <span className="admin-share-structure__radio-title">Number of co-owners</span>
                    <span className="admin-share-structure__radio-desc">Split into equal shares, investors pick how many</span>
                  </span>
                </label>
                <label
                  className={`admin-share-structure__radio-card${raisingMode === "min_investment" ? " admin-share-structure__radio-card--selected" : ""}`}
                >
                  <input
                    type="radio"
                    name={`admin-raise-${sub.id}`}
                    checked={raisingMode === "min_investment"}
                    onChange={() => setRaisingMode("min_investment")}
                    className="admin-share-structure__radio-input"
                  />
                  <span className="admin-share-structure__radio-circle" aria-hidden="true">
                    <span className="admin-share-structure__radio-circle-inner"></span>
                  </span>
                  <span className="admin-share-structure__radio-content">
                    <span className="admin-share-structure__radio-title">Minimum investment per share</span>
                    <span className="admin-share-structure__radio-desc">Set a fixed minimum, let investors buy any amount above</span>
                  </span>
                </label>
              </div>

              {raisingMode === "min_investment" ? (
                <div className="admin-share-structure__field">
                  <label className="admin-share-structure__label" htmlFor={`admin-raise-min-${sub.id}`}>Minimum investment ($)</label>
                  <input
                    id={`admin-raise-min-${sub.id}`}
                    className="admin-share-structure__input admin-share-structure__input--narrow"
                    type="text"
                    inputMode="decimal"
                    value={minInvestment}
                    onChange={(e) => setMinInvestment(filterNumericInput(e.target.value, { decimal: true }))}
                  />
                </div>
              ) : (
                <div className="admin-share-structure__field">
                  <label className="admin-share-structure__label" htmlFor={`admin-raise-shares-${sub.id}`}>Number of co-owners (shares)</label>
                  <input
                    id={`admin-raise-shares-${sub.id}`}
                    className="admin-share-structure__input admin-share-structure__input--narrow"
                    type="text"
                    inputMode="numeric"
                    value={shareCount}
                    onChange={(e) => setShareCount(filterNumericInput(e.target.value))}
                  />
                </div>
              )}

              {price > 0 ? (
                <div className="admin-share-structure__calc-preview">
                  <div className="admin-share-structure__calc-label">Share breakdown</div>
                  <div className="admin-share-structure__calc-value">
                    {preview.shares.toLocaleString()} shares
                  </div>
                  <p className="admin-share-structure__calc-breakdown">
                    <span>${perShare.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    {" "}per share · min.{" "}
                    <span>${Number(preview.minInvestment).toLocaleString()}</span>
                    {" "}investment · Total raise:{" "}
                    <span>${price.toLocaleString()}</span>
                  </p>
                </div>
              ) : null}

              <button
                type="button"
                className="admin-share-structure__btn admin-share-structure__btn--gold"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? "Saving…" : "Save & approve share structure"}
              </button>
            </div>

            {isApproved && (sub.raisingStructureApprovedByEmail || sub.raisingStructureApprovedBy) ? (
              <div className="admin-share-structure__card-footer">
                <div className="admin-share-structure__footer-meta">
                  <span>
                    Last approved by{" "}
                    <strong>
                      {sub.raisingStructureApprovedByName || sub.raisingStructureApprovedByEmail || sub.raisingStructureApprovedBy}
                    </strong>
                  </span>
                  {sub.raisingStructureApprovedAt ? (
                    <>
                      <span className="admin-share-structure__footer-dot" aria-hidden="true"></span>
                      <span>{formatAdminReviewTimestamp(sub.raisingStructureApprovedAt)}</span>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminRaiseStructureEditor;
